/**
 * Safe localStorage helper utilities
 * Provides fallback handling for corrupted or invalid localStorage data
 */

export interface LocalStorageOptions {
  fallback?: any;
  validator?: (value: any) => boolean;
  transform?: (value: any) => any;
}

/**
 * Safely get an item from localStorage with fallback handling
 */
export function safeGetItem<T = any>(key: string, options: LocalStorageOptions = {}): T {
  const { fallback = null, validator, transform } = options;

  try {
    const item = localStorage.getItem(key);
    
    // Handle null, undefined, 'null', 'undefined' strings
    if (item === null || item === 'null' || item === 'undefined') {
      return fallback;
    }

    // Try to parse JSON
    let parsed: any;
    try {
      parsed = JSON.parse(item);
    } catch (parseError) {
      console.warn(`Failed to parse localStorage item "${key}":`, parseError);
      // Clear corrupted data
      localStorage.removeItem(key);
      return fallback;
    }

    // Apply validator if provided
    if (validator && !validator(parsed)) {
      console.warn(`localStorage item "${key}" failed validation, using fallback`);
      localStorage.removeItem(key);
      return fallback;
    }

    // Apply transform if provided
    if (transform) {
      try {
        parsed = transform(parsed);
      } catch (transformError) {
        console.warn(`Failed to transform localStorage item "${key}":`, transformError);
        localStorage.removeItem(key);
        return fallback;
      }
    }

    return parsed;
  } catch (error) {
    console.warn(`Error accessing localStorage item "${key}":`, error);
    return fallback;
  }
}

/**
 * Safely set an item in localStorage with error handling
 */
export function safeSetItem(key: string, value: any): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Failed to save to localStorage "${key}":`, error);
    return false;
  }
}

/**
 * Safely remove an item from localStorage
 */
export function safeRemoveItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove localStorage item "${key}":`, error);
    return false;
  }
}

/**
 * Get array from localStorage with array validation
 */
export function safeGetArray<T = any>(key: string, fallback: T[] = []): T[] {
  return safeGetItem(key, {
    fallback,
    validator: (value) => Array.isArray(value),
  });
}

/**
 * Get boolean from localStorage with boolean validation
 */
export function safeGetBoolean(key: string, fallback: boolean = false): boolean {
  return safeGetItem(key, {
    fallback,
    validator: (value) => typeof value === 'boolean',
    transform: (value) => {
      // Handle string boolean values from old storage
      if (typeof value === 'string') {
        return value === 'true';
      }
      return value;
    },
  });
}

/**
 * Get object from localStorage with object validation
 */
export function safeGetObject<T = any>(key: string, fallback: T = {} as T): T {
  return safeGetItem(key, {
    fallback,
    validator: (value) => typeof value === 'object' && value !== null && !Array.isArray(value),
  });
}

/**
 * Get string from localStorage with string validation
 */
export function safeGetString(key: string, fallback: string = ''): string {
  return safeGetItem(key, {
    fallback,
    validator: (value) => typeof value === 'string',
  });
}

/**
 * Get number from localStorage with number validation
 */
export function safeGetNumber(key: string, fallback: number = 0): number {
  return safeGetItem(key, {
    fallback,
    validator: (value) => typeof value === 'number' && !isNaN(value),
  });
}
